import React, { FC } from 'react';
import { Box, createStyles, Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { BlogArticleFragment } from '../../../../__generated__/gatsby-types';
import colors from '../../../../themes/main-colors';
import { Link } from 'gatsby';
import { usePathContext } from '../../../../providers/PathProvider';
import { useTranslation } from '../../../../hooks/useTranslation';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spotlightCardContainer: {
      backgroundColor: ({ item }: Props) =>
        getBackgroundColor(item?.backgroundColor),
    },
    spotlightCard: {
      padding: '20px 0',
    },
    header: {
      padding: '0 30px',
    },
    spotlightCardImage: {
      display: 'flex',
      padding: '10px',
    },
    image: {
      objectFit: ({ item }: Props) =>
        `${
          item?.imageFocusHorizontal || item?.imageFocusVertical
            ? 'cover'
            : 'fill'
        }`,
      objectPosition: ({ item }: Props) =>
        `${item?.imageFocusHorizontal || '50%'} ${item?.imageFocusVertical ||
          '50%'}`,
      overflow: 'hidden',
      maxWidth: '581px',
      height: '488px',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: '430px',
      },
    },
    cardDetailsContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '20px 30px 0',
      [theme.breakpoints.down('sm')]: {
        marginLeft: 'unset',
        height: 'inherit',
        padding: '0 10px',
        marginTop: '10px',
      },
    },
    paneHeader: {
      fontSize: '14px',
      fontWeight: 'bold',
      marginBottom: '10px',
      textTransform: 'uppercase',
    },
    teaserText: {
      maxWidth: '100%',
      fontSize: '18px',
      lineHeight: theme.typography.pxToRem(22.48),
      maxHeight: theme.typography.pxToRem(103), // For truncation. Adjust based on lineHeight & # of lines
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 4,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      paddingBottom: theme.spacing(1.25),
      paddingTop: theme.spacing(1.25),
    },
    paneSubHeader: {
      textDecoration: 'underline',
      lineHeight: theme.typography.pxToRem(24),
      fontSize: theme.spacing(2.5),
    },
  }),
);

export const getBackgroundColor = (color: any | null): any | null => {
  let bgColor = colors.white;
  switch (color) {
    case 'Blue':
      bgColor = colors.riverBlue;
      break;
    case 'Grey':
      bgColor = colors.cloudGrey2;
      break;
    case 'Linen-White':
      bgColor = colors.linenWhite;
      break;
    case 'Yellow':
      bgColor = colors.lightYellow;
      break;
    default:
      bgColor = colors.white;
      break;
  }
  return bgColor;
};

interface Props {
  item: BlogArticleFragment;
  style?: React.CSSProperties | undefined;
}

const SpotlightCard: FC<Props> = (props: Props) => {
  const { item } = props;
  const { resolve } = usePathContext();
  const classes = useStyles(props);
  const { t } = useTranslation();

  return (
    <Box className={classes.spotlightCardContainer}>
      <Box className={classes.spotlightCard}>
        <Typography variant="h2" className={classes.header}>
          {t('SPOTLIGHT')}
        </Typography>
        <Box className={classes.spotlightCardImage}>
          <img
            alt=""
            src={item?.heroImage?.file?.url}
            className={classes.image}
          />
        </Box>
        <Box className={classes.cardDetailsContainer}>
          {item?.category?.name && (
            <Typography className={classes.paneHeader}>
              {item?.category?.name}
            </Typography>
          )}
          {item?.title && (
            <Link to={resolve(item?.slug)} className={classes.paneSubHeader}>
              {item?.title}
            </Link>
          )}
          {item?.teaserText && (
            <Typography className={classes.teaserText}>
              {item?.teaserText}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SpotlightCard;
